import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip — (i) icon + popup on hover.
 *
 * GH-103: the popup is rendered into document.body via Portal so it can't be
 * clipped by an ancestor's `overflow` (e.g. table wrappers with overflowX:auto,
 * which browsers treat as overflow-y:auto too, clipping the popup that
 * positions above the icon). The hover popup positions itself with
 * getBoundingClientRect on each enter so it tracks scroll/resize implicitly
 * (re-renders are driven by enter/leave).
 *
 * Existing styling (background, arrow, max-width) comes from the .tooltip-text
 * class in index.css. Inline styles below override only positioning and the
 * default visibility:hidden so the popup shows when rendered.
 */
function Tooltip({ text }) {
  const [hover, setHover] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);

  useLayoutEffect(() => {
    if (!hover || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [hover]);

  return (
    <span
      className="tooltip-wrapper"
      ref={wrapRef}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span className="tooltip-icon">i</span>
      {hover && createPortal(
        <span
          className="tooltip-text"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: 'translate(-50%, -100%)',
            visibility: 'visible',
            opacity: 1,
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

export default Tooltip;
